export type HouseholdId = string;
export type UserId = string;
export type RecipeId = string;
export type CollectionId = string;
export type IngredientId = string;
export type InstructionId = string;
export type PhotoId = string;
export type CookDateId = string;
export type DayNoteId = string;
export type NutritionFactId = string;

/**
 * Every recipe must belong to at least one collection. When concurrent edits leave a
 * recipe with none, the reconciler adopts it here. New recipes land here by default.
 * The id is a fixed string rather than a generated one so that two replicas seeding a
 * fresh document converge on one entry.
 */
export const MAIN_COLLECTION_ID: CollectionId = 'main';

export const CURRENT_SCHEMA_VERSION = 4;

export type SourceType = 'none' | 'url' | 'text';

/** Whether the listed nutrition facts describe one serving, the whole recipe, or an unknown amount. */
export type NutritionBasis = 'perServing' | 'perRecipe' | 'unknown';

export type HouseholdRole = 'owner' | 'editor' | 'viewer';

/** How tags are rendered in the UI. Storage is always as-typed; this only affects display. */
export type TagDisplayCase = 'preserve' | 'lower' | 'title';

export const DEFAULT_TAG_DISPLAY_CASE: TagDisplayCase = 'lower';

export interface Ingredient {
  readonly id: IngredientId;
  /** Fractional index. Sort by (order, id) for a total order that is stable across replicas. */
  readonly order: string;
  readonly groupHeading: string;
  readonly quantity: string;
  readonly unit: string;
  readonly name: string;
  readonly note: string;
}

export interface Instruction {
  readonly id: InstructionId;
  readonly order: string;
  readonly text: string;
}

export interface Photo {
  readonly id: PhotoId;
  readonly order: string;
  /** SHA-256 of the image bytes; the R2 object key is derived from it. */
  readonly blobId: string;
  readonly width: number;
  readonly height: number;
  readonly blurhash: string;
}

export interface NutritionFact {
  readonly id: NutritionFactId;
  /** Fractional index. Sort by (order, id) for a total order that is stable across replicas. */
  readonly order: string;
  readonly label: string;
  readonly amount: string;
  readonly unit: string;
}

/** A day the household made (or plans to make) this recipe. */
export interface CookDate {
  readonly id: CookDateId;
  /** Local calendar day, YYYY-MM-DD. */
  readonly date: string;
  /** Batch multiplier it was made at; absent for the usual 1×. */
  readonly scale?: number;
}

/** Plain text on a schedule day, for a meal that is not a recipe: "Takeout", "Leftovers". */
export interface DayNote {
  readonly id: DayNoteId;
  /** Local calendar day, YYYY-MM-DD. */
  readonly date: string;
  readonly text: string;
  /** When it was added, which orders the notes within a day. */
  readonly createdAt: number;
}

export interface ImportProvenance {
  readonly shareId: string;
  readonly sourceHouseholdName: string;
  readonly importedAt: number;
}

export interface Recipe {
  readonly id: RecipeId;
  readonly title: string;
  readonly description: string;
  readonly sourceType: SourceType;
  readonly sourceUrl: string;
  readonly sourceText: string;
  readonly servings: string;
  readonly prepMinutes: number | null;
  readonly cookMinutes: number | null;
  readonly nutritionBasis: NutritionBasis;
  readonly nutritionFacts: readonly NutritionFact[];
  readonly ingredients: readonly Ingredient[];
  readonly instructions: readonly Instruction[];
  readonly photos: readonly Photo[];
  readonly cookDates: readonly CookDate[];
  /** Collections this recipe is displayed in, sorted. Never empty in read results. */
  readonly collectionIds: readonly CollectionId[];
  /** Per-collection fractional index, so each collection has its own hand ordering. */
  readonly collectionOrder: Readonly<Record<CollectionId, string>>;
  readonly tags: readonly string[];
  readonly notes: string;
  readonly createdAt: number;
  readonly createdBy: UserId;
  readonly updatedAt: number;
  readonly updatedBy: UserId;
  readonly deletedAt: number | null;
  readonly importedFrom: ImportProvenance | null;
}

export interface Collection {
  readonly id: CollectionId;
  readonly name: string;
  readonly description: string;
  readonly icon: string;
  readonly order: string;
  /** The default collection: it cannot be deleted, but can be renamed. */
  readonly isSystem: boolean;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly deletedAt: number | null;
}

export interface NewRecipe {
  readonly title: string;
  readonly description?: string;
  readonly sourceType?: SourceType;
  readonly sourceUrl?: string;
  readonly sourceText?: string;
  readonly servings?: string;
  readonly prepMinutes?: number | null;
  readonly cookMinutes?: number | null;
  readonly nutritionBasis?: NutritionBasis;
  readonly notes?: string;
  readonly tags?: readonly string[];
  readonly nutritionFacts?: readonly NewNutritionFact[];
  readonly ingredients?: readonly NewIngredient[];
  readonly instructions?: readonly NewInstruction[];
  readonly photos?: readonly NewPhoto[];
  readonly importedFrom?: ImportProvenance | null;
  /** Only set by share import, which mints its own ids. */
  readonly id?: RecipeId;
}

export interface RecipePatch {
  readonly title?: string;
  readonly description?: string;
  readonly sourceType?: SourceType;
  readonly sourceUrl?: string;
  readonly sourceText?: string;
  readonly servings?: string;
  readonly prepMinutes?: number | null;
  readonly cookMinutes?: number | null;
  readonly nutritionBasis?: NutritionBasis;
  readonly notes?: string;
}

export interface NewIngredient {
  readonly groupHeading?: string;
  readonly quantity?: string;
  readonly unit?: string;
  readonly name: string;
  readonly note?: string;
  readonly id?: IngredientId;
}

export type IngredientPatch = Partial<Omit<NewIngredient, 'id'>>;

export interface NewNutritionFact {
  readonly label?: string;
  readonly amount?: string;
  readonly unit?: string;
  readonly id?: NutritionFactId;
}

export type NutritionFactPatch = Partial<Omit<NewNutritionFact, 'id'>>;

export interface NewInstruction {
  readonly text: string;
  readonly id?: InstructionId;
}

export type InstructionPatch = Partial<Omit<NewInstruction, 'id'>>;

export interface NewPhoto {
  readonly blobId: string;
  readonly width: number;
  readonly height: number;
  readonly blurhash?: string;
  readonly id?: PhotoId;
}

export interface NewCollection {
  readonly name: string;
  readonly description?: string;
  readonly icon?: string;
  readonly id?: CollectionId;
}

export type RecipeSort = 'title' | 'updatedAt' | 'createdAt' | 'collectionOrder' | 'favouritedAt';

export interface RecipeQuery {
  readonly collectionId?: CollectionId;
  readonly tags?: readonly string[];
  readonly search?: string;
  /** When true, returns only recipes favourited by the current user. */
  readonly onlyFavourites?: boolean;
  /** Defaults to false: trashed recipes are hidden unless explicitly asked for. */
  readonly includeDeleted?: boolean;
  /** When true, returns *only* trashed recipes (the Trash view). */
  readonly onlyDeleted?: boolean;
  readonly sort?: RecipeSort;
  readonly limit?: number;
}

export interface RevisionSummary {
  readonly revisionNo: number;
  readonly changedAt: number;
  readonly changedBy: readonly UserId[];
  readonly changedFields: readonly string[];
  readonly rebuilt: boolean;
}

export type Unsubscribe = () => void;

/** Recipes a household keeps on the free plan. Trashed recipes do not count. */
export const FREE_RECIPE_LIMIT = 12;

/**
 * The most live recipes any household can hold, whatever its plan.
 *
 * A paid plan's `recipeLimit` is null, but "unlimited" still has to end somewhere: the
 * whole book is one Yjs document that every device loads into memory. Like the plan cap,
 * trashed recipes do not count.
 */
export const MAX_HOUSEHOLD_RECIPES = 2000;

/**
 * The single `meta` key the server writes the household's entitlement to.
 *
 * One key holding one object, never separate `plan`/`recipeLimit` keys: a Y.Map is
 * last-write-wins *per key*, so separate keys can tear into a pair that never existed —
 * a free plan's limit against a paid plan's name.
 */
export const META_ENTITLEMENT_KEY = 'entitlement';

export type PlanId = 'free' | 'pro' | 'locked';

export type EntitlementSource = 'none' | 'revenuecat' | 'complimentary';

export interface Entitlement {
  readonly plan: PlanId;
  /** null means unlimited. Ignored when plan is 'locked'. */
  readonly recipeLimit: number | null;
  readonly source: EntitlementSource;
  readonly updatedAt: number;
}

/** What a household gets with no entitlement row, and what a missing meta key reads as. */
export const FREE_ENTITLEMENT: Entitlement = {
  plan: 'free',
  recipeLimit: FREE_RECIPE_LIMIT,
  source: 'none',
  updatedAt: 0,
};

/**
 * Narrows an arbitrary `meta` value to an Entitlement.
 *
 * `meta` is a plain Y.Map any client can write, so this is parsing untrusted input rather
 * than a cast. Anything malformed reads as the free plan, which fails closed.
 */
export function parseEntitlement(value: unknown): Entitlement | null {
  if (value == null || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const plan = record.plan;
  if (plan !== 'free' && plan !== 'pro' && plan !== 'locked') return null;
  const limit = record.recipeLimit;
  if (limit !== null && (typeof limit !== 'number' || !Number.isFinite(limit) || limit < 0)) {
    return null;
  }
  const source = record.source;
  if (source !== 'none' && source !== 'revenuecat' && source !== 'complimentary') return null;
  const updatedAt = typeof record.updatedAt === 'number' ? record.updatedAt : 0;
  return { plan, recipeLimit: limit, source, updatedAt };
}

/** True when two entitlements carry the same decision, ignoring `updatedAt`. */
export function sameEntitlement(a: Entitlement, b: Entitlement): boolean {
  return a.plan === b.plan && a.recipeLimit === b.recipeLimit && a.source === b.source;
}
